import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Polygon, Marker, Popup, Tooltip, Pane, useMap, useMapEvents } from 'react-leaflet';
import { Truck, Info, Search, Loader2, Navigation, ChevronLeft, ChevronRight, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
import { DELIVERY_CENTER, DELIVERY_ZONES as zones, getZoneForPosition } from '../data/deliveryZones';
import { Skeleton } from '../components/Skeleton';
const CENTER: [number, number] = DELIVERY_CENTER;
const MAP_CENTER: [number, number] = DELIVERY_CENTER;
// Per-zone accent colors. The map overlay itself stays a single brand green
// so overlapping zones don't compound into a muddy blob, but the rates drawer
// and the address-result tooltip use these colors so customers can tell
// which zone they landed in at a glance.
const ZONE_COLORS: Record<number, string> = {
  1: '#22c55e',
  2: '#06b6d4',
  3: '#eab308',
  4: '#f97316',
  5: '#ef4444' // red — farthest
};
const ZONE_OVERLAY_COLOR = '#059F40';
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
// Map-level tap handler — works anywhere on the map. Shows zone + rate for
// taps inside a zone, or "Outside Delivery Zones" for taps beyond.
function MapClickHandler({
  center,
  onClearActive
}: {
  center: [number, number];
  onClearActive: () => void;
}) {
  const map = useMapEvents({
    click(e) {
      const zoneInfo = getZoneForPosition(e.latlng.lat, e.latlng.lng);
      onClearActive();
      if (zoneInfo.zone > 0) {
        const zc = ZONE_COLORS[zoneInfo.zone] || '#059F40';
        L.popup({
          className: 'zone-popup-dark'
        }).setLatLng(e.latlng).setContent(`<div style="text-align:center;padding:4px 8px;"><p style="font-weight:bold;color:#ffffff;margin:0 0 4px;">Zone ${zoneInfo.zone}</p><p style="margin:0;font-size:14px;color:${zc};font-weight:bold;text-shadow:0 0 10px ${zc}99;">Delivery: ${zoneInfo.price}</p></div>`).openOn(map);
      } else {
        L.popup({
          className: 'zone-popup-dark'
        }).setLatLng(e.latlng).setContent('<div style="text-align:center;padding:4px 8px;"><p style="font-weight:bold;color:#ffffff;margin:0 0 4px;">Outside Delivery Zones</p><p style="margin:0;font-size:14px;"><a href="tel:206-619-3742" style="color:#F87431;font-weight:bold;text-decoration:none;">📞 Call for Quote</a></p><p style="margin:4px 0 0;font-size:12px;color:#bbbbbb;">(206) 619-3742</p></div>').openOn(map);
      }
    }
  });
  return null;
}
// Component to fly the map to a location
function FlyTo({
  position
}: {
  position: [number, number] | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 11, {
        duration: 1.5
      });
    }
  }, [position, map]);
  return null;
}
// Component to force Leaflet to recompute its size. Leaflet caches the
// container size at construction time, so any later resize (PageTransition
// finishing, font load, hero collapse, mobile orientation, keyboard) leaves
// it rendering at the wrong dimensions. We aggressively invalidate via:
//   1. ResizeObserver on the container
//   2. A RAF loop for the first second to catch the mount→animation window
//   3. Backup timeouts spread across the first ~5s
//   4. window resize listener
//   5. Document visibility change (returning to tab)
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const invalidate = () => {
      try {
        map.invalidateSize({
          pan: false,
          animate: false
        });
      } catch {
        // Map may be unmounting — ignore.
      }
    };
    // First, run invalidate aggressively in the mount window (~2s).
    // Then back off. This catches the case where Leaflet initialized at a
    // smaller size than the final container (most common on first mount).
    const timeouts = [0, 50, 100, 200, 400, 700, 1000, 1500, 2000, 3000].map(ms => setTimeout(invalidate, ms));
    // ResizeObserver — fires when the container actually changes size
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => invalidate()) : null;
    if (ro) ro.observe(container);
    // Window resize / orientation change
    window.addEventListener('resize', invalidate);
    window.addEventListener('orientationchange', invalidate);
    return () => {
      timeouts.forEach(clearTimeout);
      ro?.disconnect();
      window.removeEventListener('resize', invalidate);
      window.removeEventListener('orientationchange', invalidate);
    };
  }, [map]);
  return null;
}
// Ping data for random purchases
const PING_MATERIALS: {
  name: string;
  maxYards: number;
}[] = [{
  name: '5/8" Minus Gravel',
  maxYards: 12
}, {
  name: 'Pea Gravel',
  maxYards: 12
}, {
  name: '7/8" Drain Rock',
  maxYards: 12
}, {
  name: '3-Way Topsoil',
  maxYards: 15
}, {
  name: 'Medium Bark',
  maxYards: 15
}, {
  name: 'Coarse Bark',
  maxYards: 15
}, {
  name: '1.25" Minus',
  maxYards: 12
}, {
  name: 'Road Base',
  maxYards: 12
}, {
  name: 'Builders Sand',
  maxYards: 12
}, {
  name: 'Fill Dirt',
  maxYards: 15
}, {
  name: '2-4" Spalls',
  maxYards: 12
}];
const YARD_OPTIONS = [12, 15];
function getRandomQuantity(maxYards: number): string {
  const valid = YARD_OPTIONS.filter(y => y <= maxYards);
  return `${valid[Math.floor(Math.random() * valid.length)]} yards`;
}
function randomDateRecent(): string {
  const daysAgo = Math.floor(Math.random() * 7);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
}
type PingData = {
  id: number;
  latLng: [number, number];
  material: string;
  quantity: string;
  date: string;
  createdAt: number;
};
// Radar Overlay Component
function RadarOverlay({
  center,
  isVisible
}: {
  center: [number, number];
  isVisible: boolean;
}) {
  const map = useMap();
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pings, setPings] = useState<PingData[]>([]);
  const pingIdRef = useRef(0);
  useEffect(() => {
    if (!isVisible) return;
    const updatePosition = () => {
      const point = map.latLngToContainerPoint(center);
      setPosition({
        x: point.x,
        y: point.y
      });
    };
    updatePosition();
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);
    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [map, center, isVisible]);
  const [size, setSize] = useState(120);
  useEffect(() => {
    if (!isVisible) return;
    const updateSize = () => {
      const centerPoint = map.latLngToContainerPoint(center);
      // 50 miles radius — reaches the outer edges of Zone 5
      const edgeLat = center[0] + 50 * 1.60934 / 111.32;
      const edgePoint = map.latLngToContainerPoint([edgeLat, center[1]]);
      const pixelRadius = Math.abs(centerPoint.y - edgePoint.y);
      setSize(pixelRadius * 2);
    };
    updateSize();
    map.on('zoom', updateSize);
    map.on('zoomend', updateSize);
    return () => {
      map.off('zoom', updateSize);
      map.off('zoomend', updateSize);
    };
  }, [map, center, isVisible]);
  // Track radar angle and spawn pings when the sweep line crosses a queued position
  const radarStartRef = useRef(Date.now());
  const RADAR_DURATION = 13500; // matches CSS animation duration in ms
  const nextPingAngleRef = useRef<number>(Math.random() * 360);
  const lastSpawnAngleRef = useRef<number>(-1);
  useEffect(() => {
    if (!isVisible) return;
    radarStartRef.current = Date.now();
    const zoneRanges: [number, number][] = [[0, 10], [10, 18], [18, 28], [28, 40], [40, 50]];
    // Beyond-zone-5 cities — occasionally appear in the rotation to prove
    // we deliver past the outer zone. They cut in/out with the radar like
    // every other ping (no permanent pins).
    const beyondCities = [{
      latLng: [47.0379, -122.9007] as [number, number],
      name: 'Olympia'
    }, {
      latLng: [47.9789, -122.2021] as [number, number],
      name: 'Everett'
    }, {
      latLng: [48.0518, -122.1771] as [number, number],
      name: 'Marysville'
    }];
    setPings([]);
    // Queue the first target angle
    nextPingAngleRef.current = Math.random() * 360;
    lastSpawnAngleRef.current = -1;
    const tick = setInterval(() => {
      const elapsed = (Date.now() - radarStartRef.current) % RADAR_DURATION;
      const currentAngle = elapsed / RADAR_DURATION * 360;
      const target = nextPingAngleRef.current;
      // Check if the radar arm just crossed the target angle
      const prev = lastSpawnAngleRef.current;
      const crossed = prev >= 0 && (prev < target && currentAngle >= target || prev > currentAngle && (prev < target || currentAngle >= target));
      lastSpawnAngleRef.current = currentAngle;
      if (crossed) {
        const mat = PING_MATERIALS[Math.floor(Math.random() * PING_MATERIALS.length)];
        // ~12% chance to surface a beyond-zone-5 long-haul delivery
        const useBeyond = Math.random() < 0.12;
        let pingLatLng: [number, number];
        if (useBeyond) {
          pingLatLng = beyondCities[Math.floor(Math.random() * beyondCities.length)].latLng;
        } else {
          // Spawn ping at the target angle, random zone
          const zoneRange = zoneRanges[Math.floor(Math.random() * zoneRanges.length)];
          const dist = zoneRange[0] + Math.random() * (zoneRange[1] - zoneRange[0]);
          const distKm = dist * 1.60934;
          const angleRad = target * Math.PI / 180;
          const lat = center[0] + distKm / 111.32 * Math.cos(angleRad);
          const lng = center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad);
          pingLatLng = [lat, lng];
        }
        const newPing: PingData = {
          id: pingIdRef.current++,
          latLng: pingLatLng,
          material: mat.name,
          quantity: getRandomQuantity(mat.maxYards),
          date: randomDateRecent(),
          createdAt: Date.now()
        };
        setPings(prev => [...prev.slice(-9), newPing]);
        // Queue next ping at a random angle offset (60-240 degrees ahead)
        nextPingAngleRef.current = (target + 60 + Math.random() * 180) % 360;
      }
    }, 100);
    return () => clearInterval(tick);
  }, [center, isVisible]);
  // Remove expired pings (older than 5 seconds)
  useEffect(() => {
    if (pings.length === 0) return;
    const timer = setInterval(() => {
      setPings(prev => prev.filter(p => Date.now() - p.createdAt < 5000));
    }, 500);
    return () => clearInterval(timer);
  }, [pings.length]);
  if (!isVisible || !position) return null;
  return <>
      {/* Radar sweep */}
      <div className="absolute pointer-events-none z-[400]" style={{
      left: position.x - size / 2,
      top: position.y - size / 2,
      width: size,
      height: size
    }} data-id="element-2522">
        <div className="relative w-full h-full rounded-[50%] bg-brand-orange/[0.02]" data-id="element-2523">
          <div className="radar-sweep" data-id="element-2524" />
          <div className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 bg-brand-orange/50 rounded-full shadow-[0_0_4px_rgba(5,137,77,0.4)]" data-id="element-2525" />
        </div>
      </div>

      {/* Purchase pings — cut in / cut out, no fade */}
      {pings.map(ping => {
      const point = map.latLngToContainerPoint(ping.latLng);
      return <div key={ping.id} className="absolute pointer-events-none z-[450]" style={{
        left: point.x,
        top: point.y,
        transform: 'translate(-50%, -100%)'
      }} data-id="element-2526">
            {/* Ping dot */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-brand-orange rounded-full shadow-[0_0_6px_rgba(5,137,77,0.6)]" data-id="element-2527" />
            {/* Info card */}
            <div className="mb-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 whitespace-nowrap shadow-xl" data-id="element-2528">
              <p className="text-white text-xs font-semibold" data-id="element-2529">
                {ping.quantity} — {ping.material}
              </p>
              <p className="text-[10px]" style={{
            color: '#808080'
          }} data-id="element-2530">
                Purchased {ping.date}
              </p>
            </div>
          </div>;
    })}
    </>;
}
// Custom pulsating red marker for user location
const pulsatingIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div class="pulsating-pin"><div class="pulsating-pin-dot"></div></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -15]
});
// Heuristic: looks like a real street address (number + street name)
function isAddressLikelyValid(addr: string): boolean {
  const trimmed = addr.trim();
  if (trimmed.length < 8) return false;
  // Must contain a house number (1+ digits)
  if (!/\d/.test(trimmed)) return false;
  // Must contain a street name word with at least 3 letters
  const letterWords = trimmed.match(/[A-Za-z]{3,}/g);
  if (!letterWords || letterWords.length < 1) return false;
  // Must have at least 2 space-separated tokens (number + name)
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return false;
  return true;
}
export function DeliveryZones() {
  const [address, setAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [searchResult, setSearchResult] = useState<{
    zone: number;
    price: string;
    color: string;
    distance: number;
    displayName: string;
  } | null>(null);
  const [searchError, setSearchError] = useState('');
  const hasAutoSearched = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [mountMap, setMountMap] = useState(false);
  // If the user got here from the Hero estimator (?from=estimator), build a
  // return URL that preserves their address/material/yards so they can jump
  // back without re-entering everything. Hero's mount-effect re-hydrates the
  // form from these params on arrival.
  const [estimatorReturnHref, setEstimatorReturnHref] = useState<string | null>(null);
  useEffect(() => {
    // Read query params from the URL. Works with BrowserRouter (params live
    // in location.search) and stays defensive against any lingering hash
    // links (?...) shared from the old HashRouter era.
    const search = window.location.search || (window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : '');
    if (!search) return;
    const params = new URLSearchParams(search);
    if (params.get('from') !== 'estimator') return;
    const next = new URLSearchParams();
    const addr = params.get('address');
    const mat = params.get('material');
    const qty = params.get('qty');
    if (addr) next.set('address', addr);
    if (mat) next.set('material', mat);
    if (qty) next.set('qty', qty);
    const qs = next.toString();
    setEstimatorReturnHref(`/${qs ? `?${qs}` : ''}#estimator`);
  }, []);
  // Delay mounting the map until the layout is established to prevent partial tile rendering
  useEffect(() => {
    const timer = setTimeout(() => setMountMap(true), 100);
    return () => clearTimeout(timer);
  }, []);
  // Fallback: always dismiss the skeleton after 1.5s in case whenReady doesn't
  // fire (it's been unreliable in some configurations).
  useEffect(() => {
    const t = setTimeout(() => setMapLoaded(true), 1500);
    return () => clearTimeout(t);
  }, []);
  const addressIsValid = isAddressLikelyValid(address);
  const handleSearch = useCallback(async () => {
    if (!address.trim()) return;
    if (!isAddressLikelyValid(address)) {
      setSearchError('Please enter a complete address — house number and street name (e.g. "123 Main St, Tacoma").');
      setSearchResult(null);
      setUserPosition(null);
      return;
    }
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    setUserPosition(null);
    try {
      // Only append ", WA" if the address doesn't already reference Washington —
      // a doubled state reference (e.g. "...Seattle, WA 98107, WA") can cause
      // Nominatim to return zero results for otherwise valid addresses.
      const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
      const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us&addressdetails=1`);
      const data = await res.json();
      if (!data || data.length === 0) {
        setSearchError('Address not found. Please try a more specific address.');
        setIsSearching(false);
        return;
      }
      const result = data[0];
      const lat = parseFloat(result.lat);
      const lon = parseFloat(result.lon);
      const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
      // Sanity check 1: must be in WA
      const state = result.address?.state;
      if (state && state !== 'Washington') {
        setSearchError('That address is outside Washington. Please double-check your input.');
        setIsSearching(false);
        return;
      }
      // Sanity check 2: must be reasonably close (within 100 miles of our center)
      if (distance > 100) {
        setSearchError('We couldn\'t find that address near our delivery area. Please add a city (e.g. "123 Main St, Tacoma").');
        setIsSearching(false);
        return;
      }
      // The user-input already passed isAddressLikelyValid (house number + street),
      // and Nominatim returned a hit in WA within 100 miles — trust it. Earlier
      // we had a stricter type/house_number check here, but Nominatim often
      // returns valid Seattle addresses as type='way' or 'residential' with no
      // house_number field, which legitimately exist but failed the check.
      const zoneInfo = getZoneForPosition(lat, lon);
      setUserPosition([lat, lon]);
      setSearchResult({
        ...zoneInfo,
        distance: Math.round(distance * 10) / 10,
        displayName: result.display_name
      });
    } catch {
      setSearchError('Something went wrong. Please try again.');
    }
    setIsSearching(false);
  }, [address]);
  // Auto-search if address is passed via URL query param
  useEffect(() => {
    if (hasAutoSearched.current) return;
    // Read query params from BrowserRouter (location.search). Falls back to
    // parsing the hash for backwards-compat with any old HashRouter share links.
    const search = window.location.search || (window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : '');
    const params = new URLSearchParams(search);
    const urlAddress = params.get('address');
    if (urlAddress) {
      setAddress(urlAddress);
      hasAutoSearched.current = true;
    }
  }, []);
  // Trigger search when address is set from URL param
  useEffect(() => {
    if (hasAutoSearched.current && address && !searchResult && !isSearching && !searchError) {
      handleSearch();
    }
  }, [address, hasAutoSearched.current, searchResult, isSearching, searchError, handleSearch]);
  return <main className="relative w-full h-[calc(100dvh-80px)] lg:h-[calc(100dvh-113px)] overflow-hidden bg-[#373737]" data-id="element-2531">
      {/* Full-page Map Container — fills viewport on all screen sizes */}
      <div className="absolute inset-0 z-0" data-id="element-2532">
        {!mapLoaded && <Skeleton className="absolute inset-0 z-10 pointer-events-none" data-id="element-2533" />}
        {mountMap && <MapContainer center={MAP_CENTER} zoom={10} scrollWheelZoom={true} className="w-full h-full" zoomControl={false} whenReady={() => setMapLoaded(true)} data-id="element-2534">
            <TileLayer attribution="&copy; Google Maps" url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" data-id="element-2535" />
            <FlyTo position={userPosition} data-id="element-2536" />
            <MapInvalidator data-id="element-2537" />
            <MapClickHandler center={CENTER} onClearActive={() => {}} data-id="element-2538" />

            <RadarOverlay center={CENTER} isVisible={!userPosition} data-id="element-2539" />

            {/* Zone polygons — wrapped in a Pane with a single shared opacity
              so overlapping zones do NOT compound. The whole region reads as
              one uniform green shape regardless of zone depth. The drawer
              and address-result tooltip carry the per-zone color cue. */}
            <Pane name="zones-pane" style={{
          opacity: 0.13
        }} data-id="element-2540">
              {[...zones].reverse().map(zone => {
            const color = ZONE_OVERLAY_COLOR;
            return zone.polygons.map((polygonCoords, idx) => <Polygon key={`${zone.id}-${idx}`} positions={polygonCoords as [number, number][]} pane="zones-pane" pathOptions={{
              color: color,
              weight: 0,
              opacity: 0,
              fillColor: color,
              fillOpacity: 1,
              interactive: false
            }} data-id="element-2541" />);
          })}
            </Pane>

            {userPosition && <Marker position={userPosition} icon={pulsatingIcon} data-id="element-2542">
                {searchResult && <Tooltip permanent direction="right" offset={[12, 0]} className={`zone-speech-bubble ${searchResult.zone > 0 ? `zone-${searchResult.zone}` : 'zone-outside'}`} data-id="element-2543">
                    <div className="flex items-center gap-2" data-id="element-2544">
                      <span className="font-bold text-white text-sm" data-id="element-2545">
                        {searchResult.zone > 0 ? `Zone ${searchResult.zone}` : 'Outside Zones'}
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20 text-white" data-id="element-2546">
                        {searchResult.zone > 0 ? searchResult.price === 'FREE' ? 'Free Delivery' : `Delivery: ${searchResult.price}` : 'Call for Quote'}
                      </span>
                    </div>
                  </Tooltip>}
                <Popup data-id="element-2547">
                  <div className="text-center" data-id="element-2548">
                    <p className="font-bold text-brand-charcoal" data-id="element-2549">
                      Your Location
                    </p>
                    {searchResult && <p className="text-sm text-stone-600" data-id="element-2550">
                        Zone {searchResult.zone || 'Outside'}
                      </p>}
                  </div>
                </Popup>
              </Marker>}
          </MapContainer>}
      </div>

      {/* Top Gradient Overlay (Desktop) */}
      <div className="hidden lg:block absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#373737]/70 via-[#373737]/30 to-transparent z-10 pointer-events-none" data-id="element-2551" />

      {/* Floating UI Container */}
      <div className="absolute inset-0 z-20 pointer-events-none" data-id="element-2552">
        {/* Back to Estimate — only shows when the user arrived from the Hero
            estimator. Tapping it returns them with all their inputs preserved. */}
        <AnimatePresence data-id="element-2553">
          {estimatorReturnHref && <motion.div key="back-to-estimate" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.25
        }} className="absolute top-4 right-4 z-50 pointer-events-auto" data-id="element-2554">
              <Link to={estimatorReturnHref} className="group inline-flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors" data-id="element-2555">
                <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" data-id="element-2556" />
                <span className="whitespace-nowrap underline underline-offset-4 decoration-white/30 group-hover:decoration-white/70" data-id="element-2557">
                  Back to estimate
                </span>
              </Link>
            </motion.div>}
        </AnimatePresence>

        {/* Compact free-floating search — fixed to the viewport bottom so it
            never gets pushed out of view by the page-level pb-16 padding that
            sits below the map on mobile. Includes safe-area inset for devices
            with a home indicator. */}
        <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-sm px-4 z-50" style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem + 100px)'
      }} data-id="element-2558">
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="pointer-events-auto" data-id="element-2559">
            <form onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }} className="flex items-center bg-[#474747]/95 backdrop-blur-md border border-white/15 shadow-2xl rounded-full overflow-hidden" data-id="element-2560">
              <AddressAutocomplete value={address} onChange={setAddress} onSelect={s => {
              setAddress(s.fullAddress);
              setTimeout(() => handleSearch(), 0);
            }} placeholder="Enter delivery address..." autoComplete="street-address" variant="dark" dropDirection="up" containerClassName="flex-1" leftIcon={<Navigation className="w-3.5 h-3.5 text-stone-400" data-id="element-2562" />} inputClassName="w-full pl-9 pr-3 py-3 bg-transparent text-white placeholder-stone-400 focus:outline-none text-sm border-none focus:ring-0" data-id="element-2561" />
              <button type="submit" disabled={isSearching || !addressIsValid} aria-label="Find Zone" className="m-1 px-3.5 py-2.5 bg-brand-orange hover:bg-brand-orange-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold transition-all flex items-center justify-center gap-1.5 shrink-0 rounded-full text-xs" data-id="element-2563">
                {isSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-2564" /> : <Search className="w-3.5 h-3.5" data-id="element-2565" />}
              </button>
            </form>

            {/* Search Result Error — sits ABOVE the bottom search bar */}
            <AnimatePresence mode="wait" data-id="element-2566">
              {searchError && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="absolute bottom-full mb-2 left-0 right-0 mx-4 p-2.5 bg-red-500/20 backdrop-blur-md border border-red-500/30 rounded-xl text-red-200 text-center text-xs font-medium" data-id="element-2567">
                  {searchError}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Unified pull-out Rates drawer — works on all screen sizes */}
        {(() => {
        const legendBody = <>
              <h2 className="text-sm mb-3 flex items-center font-[600] font-[Inter,_sans-serif] text-[#059F40]" data-id="element-2568">
                <Truck className="w-4 h-4 mr-2 text-[#059F40]" data-id="element-2569" />
                Delivery Rates
              </h2>

              <div className="space-y-1" data-id="element-2570">
                {[{
              id: 1,
              price: '+$50'
            }, {
              id: 2,
              price: '+$80'
            }, {
              id: 3,
              price: '+$120'
            }, {
              id: 4,
              price: '+$150'
            }, {
              id: 5,
              price: '+$180'
            }].map(z => {
              const isMatched = searchResult?.zone === z.id;
              return <div key={z.id} className={`flex items-center justify-between py-1.5 px-2 rounded-md transition-colors ${isMatched ? 'bg-white/10' : 'hover:bg-white/5'}`} data-id="element-2571">
                      <div className="flex items-center" data-id="element-2572">
                        <div className="w-2.5 h-2.5 rounded-full mr-2.5 shrink-0" style={{
                    backgroundColor: ZONE_COLORS[z.id],
                    boxShadow: isMatched ? `0 0 10px ${ZONE_COLORS[z.id]}` : 'none'
                  }} data-id="element-2573" />
                        <p className="text-xs" style={{
                    color: isMatched ? ZONE_COLORS[z.id] : 'rgb(231 229 228)',
                    fontWeight: isMatched ? 600 : 400
                  }} data-id="element-2574">
                          Zone {z.id}
                        </p>
                      </div>
                      <span className="text-[11px] font-medium" style={{
                  color: isMatched ? ZONE_COLORS[z.id] : 'rgb(214 211 209)'
                }} data-id="element-2575">
                        {z.price}
                      </span>
                    </div>;
            })}
              </div>

              <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-id="element-2576">
                <div className="flex items-start" data-id="element-2577">
                  <Info className="w-3.5 h-3.5 text-brand-orange mr-1.5 shrink-0 mt-0.5" data-id="element-2578" />
                  <p className="text-[11px] text-stone-300 leading-snug" data-id="element-2579">
                    Outside Zone 5?{' '}
                    <a href="tel:206-619-3742" className="text-brand-orange font-bold hover:underline" data-id="element-2580">
                      Call (206) 619-3742
                    </a>{' '}
                    for a custom quote.
                  </p>
                </div>
                <p className="text-[10px] text-stone-400 italic leading-snug" data-id="element-2581">
                  *No delivery to Vashon, Maury, or Fox Island
                </p>
              </div>
            </>;
        return <motion.div initial={{
          x: -220
        }} animate={{
          x: isLegendOpen ? 0 : -220
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 30
        }} className="absolute top-20 lg:top-24 left-0 z-40 pointer-events-auto flex items-start" data-id="element-2582">
              {/* Panel */}
              <div className={`bg-[#474747]/95 backdrop-blur-xl rounded-br-2xl p-4 border border-l-0 shadow-2xl w-[220px] transition-colors ${isLegendOpen ? 'border-brand-orange' : 'border-white/10'}`} data-id="element-2583">
                {legendBody}
              </div>

              {/* Tab handle */}
              <button type="button" onClick={() => setIsLegendOpen(v => !v)} aria-label={isLegendOpen ? 'Collapse rates' : 'Show rates'} aria-expanded={isLegendOpen} className="group bg-[#474747]/95 backdrop-blur-xl hover:bg-[#555555]/95 border border-l-0 border-brand-orange transition-colors rounded-br-xl shadow-2xl py-6 px-2.5 flex flex-col items-center gap-3 -ml-px" data-id="element-2584">
                {isLegendOpen ? <ChevronLeft className="w-5 h-5 text-brand-orange" data-id="element-2585" /> : <ChevronRight className="w-5 h-5 text-brand-orange" data-id="element-2586" />}
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange" style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)'
            }} data-id="element-2587">
                  Rates
                </span>
                <motion.div animate={{
              opacity: [0.7, 1, 0.7],
              scale: [1, 1.15, 1]
            }} transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="text-brand-orange" data-id="element-2588">
                  <Truck className="w-5 h-5" data-id="element-2589" />
                </motion.div>
              </button>
            </motion.div>;
      })()}
      </div>
    </main>;
}